import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Box, ClickAwayListener, Paper } from '@mui/material';
import { ONE_PAGE_LINK_STATUS } from 'constants/businessPlan';
import * as ROUTES from 'constants/paths';
import { useBreakpoints } from 'hooks';
import { useGetLinkPublicStatus } from 'services/businessPlan/hooks/useGetLinkPublicStatus';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { PDFOrShareTooltip } from 'pages/BusinessToolPage/BusinessToolHeader/components/PDFOrShareTooltip/PDFOrShareTooltip';
import { useAIActionHandler } from 'pages/BusinessToolPage/components/BusinessToolAiProvider/hooks/useAIActionHandler';

import { ReactComponent as ChainsIcon } from 'assets/icons/ChainsIcon.svg';
import { ReactComponent as VisibilityIcon } from 'assets/icons/Visibility.svg';

import { useManageDocumentShare } from 'hooks/useManageDocumentShare';
import { useOnePageShareRestrictions } from 'hooks/useOnePageShareRestrictions';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { BlackToast } from 'components/_basic/BlackToast/BlackToast';
import { MenuList } from 'components/ShareDocumentButton/components/MenuList';
import { ShareButton } from 'components/ShareDocumentButton/components/ShareButton';
import { IShareMenuItem } from 'components/ShareDocumentButton/types';

import { copyOnePageLink } from 'helpers/viewDocHandler';

import styles from './ShareOnePage.module.scss';

export const ShareOnePage: FC = () => {
  const { isShareMenuOpened, onOpenMenu, onClickAway } = useOnePageShareRestrictions();
  const { id: uuid = '' } = useParams();
  const { isMobile } = useBreakpoints();
  const { handleActionWithAICheck } = useAIActionHandler();
  const { t } = useTranslation();

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  const { data: linkStatus } = useGetLinkPublicStatus({ id: uuid, enabled: true });

  const isCopyLinkDisabled = linkStatus?.openLinkStatus === ONE_PAGE_LINK_STATUS.RESTRICTED;

  const { manageOnePageShare } = useManageDocumentShare({ uuid, linkStatus });

  const copyLinkClickHandler = () => {
    copyOnePageLink(uuid);
    toast(<BlackToast />, {
      position: 'top-center',
    });
  };

  const displayItems: IShareMenuItem[] = [
    {
      title: t('copyLink'),
      icon: <ChainsIcon />,
      disabled: isCopyLinkDisabled,
      clickHandler: copyLinkClickHandler,
      buttonTitle: t('manage'),
      buttonClickHandler: manageOnePageShare,
    },
    {
      title: t('viewOnlineOnePage'),
      icon: <VisibilityIcon />,
      clickHandler: () => window.open(ROUTES.ONE_PAGE_PROTECTED, '_blank'),
    },
  ];

  const openMenuHandler = () => {
    if (!isStandardOrLightTariff) {
      return;
    }
    handleActionWithAICheck(onOpenMenu);
  };

  return (
    <Box className={styles.wrapper}>
      <PDFOrShareTooltip isPdfTooltip={false} leaveDelay={isMobile ? 6500 : 200}>
        <Box>
          <ShareButton onClick={openMenuHandler} shouldDocUpdate={false} isDocCreated={false} isDisabled={false} />
        </Box>
      </PDFOrShareTooltip>
      {isShareMenuOpened && (
        <ClickAwayListener onClickAway={onClickAway}>
          <Paper className={styles.menuWrapper} elevation={2}>
            <MenuList
              displayItems={displayItems}
              clickAwayHandler={onClickAway}
              isDocCreated={false}
              shouldDocUpdate={false}
            />
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
};
